(() => {
  const QUERY = `
    query FirstPassReviews($type: String!, $first: Int!, $after: String) {
      metaobjects(type: $type, first: $first, after: $after) {
        nodes {
          id
          handle
          fields {
            key
            type
            value
            reference {
              __typename
              ... on Product {
                id
                handle
                title
                featuredImage {
                  url
                  altText
                }
              }
              ... on MediaImage {
                image {
                  url
                  altText
                }
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  const STORAGE_PREFIX = "firstpass_reviews_seen_v1";

  const shuffle = (arr) => {
    const clone = [...arr];
    for (let i = clone.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [clone[i], clone[j]] = [clone[j], clone[i]];
    }
    return clone;
  };

  const escapeHtml = (str = "") =>
    String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const parseFieldMap = (fields = []) => {
    const map = {};
    fields.forEach((f) => { map[f.key] = f; });
    return map;
  };

  const parseRating = (raw) => {
    if (!raw) return 0;
    try {
      const p = JSON.parse(raw);
      if (p && p.value) {
        const n = parseFloat(p.value);
        return Number.isFinite(n) ? Math.max(0, Math.min(5, n)) : 0;
      }
    } catch (_) {}
    const n = parseFloat(String(raw).replace(/[^\d.]/g, ""));
    return Number.isFinite(n) ? Math.max(0, Math.min(5, n)) : 0;
  };

  const formatDate = (raw) => {
    if (!raw) return "";
    const d = new Date(raw);
    return isNaN(d.getTime()) ? raw : d.toISOString().slice(0, 10);
  };

  const boolVal = (raw) =>
    ["true", "1", "yes"].includes(String(raw).trim().toLowerCase());

  const getContextKey = ({ pageType, productHandle }) =>
    pageType === "product" && productHandle
      ? `product:${productHandle}`
      : "generic:home";

  const getSeenSet = (contextKey) => {
    try {
      const raw = localStorage.getItem(`${STORAGE_PREFIX}:${contextKey}`);
      const parsed = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch (_) { return new Set(); }
  };

  const saveSeenSet = (contextKey, set) => {
    try {
      localStorage.setItem(`${STORAGE_PREFIX}:${contextKey}`, JSON.stringify([...set]));
    } catch (_) {}
  };

  const mapNode = (node) => {
    const f = parseFieldMap(node.fields || []);
    const product = f.product?.reference?.__typename === "Product"
      ? f.product.reference : null;
    return {
      id: node.id,
      handle: node.handle,
      reviewerName: f.reviewer_name?.value || "",
      review: f.review?.value || "",
      rating: parseRating(f.rating?.value),
      reviewDate: formatDate(f.review_date?.value),
      location: f.location?.value || "",
      verifiedCustomer: boolVal(f.verified_customer?.value),
      source: f.source?.value || "",
      reply: f.reply?.value || "",
      product: product ? {
        handle: product.handle,
        title: product.title,
        imageUrl: product.featuredImage?.url || "",
        imageAlt: product.featuredImage?.altText || product.title || ""
      } : null
    };
  };

  const starsHtml = (rating) =>
    Array.from({ length: 5 }, (_, i) =>
      `<span class="fp-star" style="${i < Math.round(rating) ? "" : "opacity:.22"}">★</span>`
    ).join("");

  const cardHtml = (r) => `
    <div class="fp-slide" data-review-id="${escapeHtml(r.id)}">
      <article class="fp-card">
        <div class="fp-card-inner">
          <div class="fp-main">
            <div class="fp-person">
              <div class="fp-person-icon">👤</div>
              <div>
                <div class="fp-name">${escapeHtml(r.reviewerName)}</div>
                <div class="fp-location">${escapeHtml(r.location)}</div>
              </div>
            </div>
            <div class="fp-stars" aria-label="Rating: ${r.rating} out of 5">${starsHtml(r.rating)}</div>
            <div class="fp-review">${escapeHtml(r.review)}</div>
            <div class="fp-divider"></div>
            ${r.reply ? `
              <div class="fp-reply">
                <div class="fp-reply-label">FirstPass Reply</div>
                <div class="fp-reply-text">${escapeHtml(r.reply)}</div>
              </div>` : ""}
          </div>
          <aside class="fp-side">
            <div class="fp-meta-box">
              <div class="fp-verified ${r.verifiedCustomer ? "true" : "false"}">
                ${r.verifiedCustomer ? "✔ Verified Customer" : "✘ Not Verified"}
              </div>
              <div class="fp-meta-grid">
                <div>
                  <span class="fp-meta-label">Source</span>
                  <span class="fp-meta-value">${escapeHtml(r.source)}</span>
                </div>
                <div>
                  <span class="fp-meta-label">Reviewed</span>
                  <span class="fp-meta-value">${escapeHtml(r.reviewDate)}</span>
                </div>
                <div>
                  <span class="fp-meta-label">Product</span>
                  <div class="fp-product-mini">
                    ${r.product?.imageUrl ? `
                      <div class="fp-product-thumb">
                        <img src="${escapeHtml(r.product.imageUrl)}" alt="${escapeHtml(r.product.imageAlt)}" loading="lazy">
                      </div>` : ""}
                    <span class="fp-meta-value">${escapeHtml(r.product?.title || "FirstPass")}</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </article>
    </div>`;

  async function gql({ storeDomain, token, apiVersion, query, variables }) {
    const res = await fetch(`https://${storeDomain}/api/${apiVersion}/graphql.json`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token
      },
      body: JSON.stringify({ query, variables })
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const json = await res.json();
    if (json.errors?.length) throw new Error(json.errors.map(e => e.message).join(", "));
    return json.data;
  }

  async function fetchAll(config) {
    let after = null, hasNext = true;
    const all = [];
    while (hasNext) {
      const data = await gql({ ...config, query: QUERY, variables: { type: config.metaobjectType, first: 100, after } });
      const conn = data?.metaobjects;
      (conn?.nodes || []).forEach(n => all.push(mapNode(n)));
      hasNext = Boolean(conn?.pageInfo?.hasNextPage);
      after = conn?.pageInfo?.endCursor || null;
      if (!after) break;
    }
    return all.filter(r => r.review && r.reviewerName);
  }

  function pickBatch(all, { pageType, productHandle, batchSize }) {
    all = shuffle(all);
    const ctxKey = getContextKey({ pageType, productHandle });
    const seen = getSeenSet(ctxKey);
    const specific = productHandle ? all.filter(r => r.product?.handle === productHandle) : [];
    const pool = pageType === "product" && specific.length
      ? [...shuffle(specific), ...shuffle(all.filter(r => r.product?.handle !== productHandle))]
      : shuffle(all);
    let unseen = pool.filter(r => !seen.has(r.id));
    if (!unseen.length) { saveSeenSet(ctxKey, new Set()); unseen = pool; }
    const chosen = [];
    const ids = new Set();
    for (const r of unseen) {
      if (chosen.length >= batchSize) break;
      if (!ids.has(r.id)) { chosen.push(r); ids.add(r.id); }
    }
    for (const r of pool) {
      if (chosen.length >= batchSize) break;
      if (!ids.has(r.id)) { chosen.push(r); ids.add(r.id); }
    }
    const nextSeen = getSeenSet(ctxKey);
    chosen.forEach(r => nextSeen.add(r.id));
    saveSeenSet(ctxKey, nextSeen);
    return chosen;
  }

  class Carousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector("[data-fp-track]");
      this.nav = root.querySelector("[data-fp-nav]");
      this.dots = root.querySelector("[data-fp-dots]");
      this.loading = root.querySelector("[data-fp-loading]");
      this.empty = root.querySelector("[data-fp-empty]");
      this.index = 0;
      this.timer = null;
      this.batch = [];
    }

    get cfg() {
      const d = this.root.dataset;
      return {
        storeDomain: d.storeDomain,
        token: d.storefrontToken,
        apiVersion: d.apiVersion || "2026-01",
        metaobjectType: d.metaobjectType || "firstpass_reviews",
        pageType: d.pageType || "generic",
        productHandle: d.productHandle || "",
        batchSize: parseInt(d.batchSize || "12", 10),
        rotationMs: parseInt(d.rotationMs || "7000", 10)
      };
    }

    async init() {
      try {
        if (!this.cfg.token) throw new Error("Missing Storefront API token");
        const all = await fetchAll(this.cfg);
        this.batch = pickBatch(all, this.cfg);
        if (!this.batch.length) return this.showEmpty();
        this.render();
        this.bind();
        this.update();
        this.startAuto();
      } catch (e) {
        console.error("[FirstPass Reviews]", e);
        this.showEmpty();
      }
    }

    showEmpty() {
      if (this.loading) this.loading.hidden = true;
      if (this.empty) this.empty.hidden = false;
    }

    render() {
      this.track.innerHTML = this.batch.map(cardHtml).join("");
      this.dots.innerHTML = this.batch.map((_, i) =>
        `<button class="fp-dot${i === 0 ? " active" : ""}" type="button" data-index="${i}" aria-label="Review ${i + 1}"></button>`
      ).join("");
      if (this.loading) this.loading.hidden = true;
      if (this.nav) this.nav.hidden = this.batch.length < 2;
    }

    bind() {
      this.root.querySelector(".fp-prev")?.addEventListener("click", () => { this.goTo(this.index - 1); this.startAuto(); });
      this.root.querySelector(".fp-next")?.addEventListener("click", () => { this.goTo(this.index + 1); this.startAuto(); });
      this.dots?.addEventListener("click", e => {
        const d = e.target.closest("[data-index]");
        if (d) { this.goTo(parseInt(d.dataset.index, 10)); this.startAuto(); }
      });
      this.root.addEventListener("mouseenter", () => this.stopAuto());
      this.root.addEventListener("mouseleave", () => this.startAuto());
      let tx = 0;
      this.root.addEventListener("touchstart", e => { tx = e.changedTouches[0].clientX; this.stopAuto(); }, { passive: true });
      this.root.addEventListener("touchend", e => {
        const dx = e.changedTouches[0].clientX - tx;
        if (Math.abs(dx) > 40) this.goTo(this.index + (dx < 0 ? 1 : -1));
        this.startAuto();
      }, { passive: true });
      window.addEventListener("resize", () => this.update());
    }

    goTo(n) {
      const t = this.batch.length;
      if (!t) return;
      this.index = (n + t) % t;
      this.update();
    }

    update() {
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
      this.dots.querySelectorAll(".fp-dot").forEach((d, i) => d.classList.toggle("active", i === this.index));
    }

    startAuto() {
      this.stopAuto();
      if (this.batch.length < 2) return;
      this.timer = setInterval(() => this.goTo(this.index + 1), this.cfg.rotationMs);
    }

    stopAuto() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }
  }

  function init() {
    document.querySelectorAll("[data-firstpass-review-carousel]").forEach(el => {
      if (el.dataset.fpMounted === "true") return;
      el.dataset.fpMounted = "true";
      new Carousel(el).init();
    });
  }

  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", init)
    : init();
})();
